import { useEffect, useState } from 'react';
import { Plus, Edit, Power, MapPin } from 'lucide-react';
import { supabase } from '../services/supabase';
import { loadAllSedes, type Sede } from '../services/sedes';
import Modal from '../components/ui/Modal';

// Gestión de sedes (S-0 multi-sede). CRUD simple sobre la tabla `sedes` (existe
// desde 001, con RLS: SELECT público, mutaciones solo admin). Modelo: la sede es
// atributo + filtro; TODOS ven todas, sin RLS por sede, sin tocar roles. No hay
// borrado definitivo: una sede se retira con `active=false` (soft) para no dejar
// huérfanos en grupos/perfiles que la referencian (FK ON DELETE SET NULL).

interface SedeForm {
  name: string;
  city: string;
  address: string;
  active: boolean;
}
const FORM_EMPTY: SedeForm = { name: '', city: '', address: '', active: true };

export default function SedesPage() {
  const [sedes, setSedes] = useState<Sede[]>([]);
  const [loading, setLoading] = useState(true);
  // «No hay sedes todavía» invita a crear la primera. Con la lectura caída
  // eso se lo dice a un club que ya las tiene.
  const [errorCarga, setErrorCarga] = useState<string | null>(null);
  const [modal, setModal] = useState(false);
  const [editTarget, setEditTarget] = useState<Sede | null>(null);
  const [form, setForm] = useState<SedeForm>(FORM_EMPTY);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  // El interruptor de la fila no pasa por el modal, así que `saveError` no le
  // sirve: necesita su propio sitio donde contar que la base rechazó el
  // cambio que ya se había pintado en pantalla.
  const [listError, setListError] = useState('');

  useEffect(() => {
    load();
  }, []);

  async function load() {
    setErrorCarga(null);
    const { sedes: filas, error } = await loadAllSedes();
    if (error) {
      setErrorCarga(error);
      setSedes([]);
      setLoading(false);
      return;
    }
    setSedes(filas);
    setLoading(false);
  }

  function openCreate() {
    setEditTarget(null);
    setForm(FORM_EMPTY);
    setSaveError('');
    setModal(true);
  }
  function openEdit(s: Sede) {
    setEditTarget(s);
    setForm({
      name: s.name,
      city: s.city ?? '',
      address: s.address ?? '',
      active: s.active,
    });
    setSaveError('');
    setModal(true);
  }

  async function handleSave() {
    setSaveError('');
    const name = form.name.trim();
    if (!name) return setSaveError('El nombre del campo/polideportivo es obligatorio.');

    setSaving(true);
    const payload = {
      name,
      city: form.city.trim() || null,
      address: form.address.trim() || null,
      active: form.active,
    };
    const res = editTarget
      ? await supabase.from('sedes').update(payload).eq('id', editTarget.id)
      : await supabase.from('sedes').insert(payload);
    setSaving(false);
    if (res.error) {
      setSaveError(`No se pudo guardar: ${res.error.message}`);
      return;
    }
    await load();
    setModal(false);
  }

  async function toggleSede(s: Sede) {
    const { error } = await supabase.from('sedes').update({ active: !s.active }).eq('id', s.id);
    // El error se comprueba ANTES de tocar el estado: pintar la fila como
    // desactivada cuando la base ha dicho que no es peor que no pintar nada,
    // porque el cambio falso sobrevive hasta la siguiente recarga.
    if (error) {
      setListError(
        `No se pudo ${s.active ? 'desactivar' : 'activar'} "${s.name}": ${error.message}`
      );
      return;
    }
    setListError('');
    setSedes((prev) => prev.map((x) => (x.id === s.id ? { ...x, active: !x.active } : x)));
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-oswald text-2xl text-white uppercase tracking-wide">Sedes</h1>
          <p className="text-text-secondary text-sm">
            Instalaciones donde se imparten las sesiones. La sede es un atributo de cada grupo; se
            usa para mostrar y filtrar. Retirar una sede es «Desactivar» (no se borra para no dejar
            grupos sin sede).
          </p>
        </div>
        <button
          onClick={openCreate}
          className="btn-gold flex items-center gap-2"
          data-testid="sede-new"
        >
          <Plus size={16} /> Nueva sede
        </button>
      </div>

      {listError && (
        <p
          className="text-error text-sm border border-error/30 rounded px-3 py-2"
          data-testid="sedes-list-error"
        >
          {listError}
        </p>
      )}

      {loading ? (
        <div className="flex justify-center h-48 items-center">
          <div className="w-8 h-8 border-2 border-gold-primary/30 border-t-gold-primary rounded-full animate-spin" />
        </div>
      ) : errorCarga ? (
        <div className="card text-center py-12 space-y-3" data-testid="sedes-error">
          <p className="text-error text-sm">No se han podido cargar las sedes. {errorCarga}</p>
          <button className="btn-ghost text-xs" onClick={load} data-testid="sedes-reintentar">
            Reintentar
          </button>
        </div>
      ) : sedes.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-text-secondary text-sm">No hay sedes todavía.</p>
        </div>
      ) : (
        <div className="card p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-text-secondary border-b border-bg-border">
                <th className="p-3 font-inter font-normal">Nombre</th>
                <th className="p-3 font-inter font-normal">Ciudad</th>
                <th className="p-3 font-inter font-normal">Dirección</th>
                <th className="p-3 font-inter font-normal w-24">Estado</th>
                <th className="p-3 font-inter font-normal w-24 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {sedes.map((s) => (
                <tr
                  key={s.id}
                  data-testid={`sede-row-${s.id}`}
                  className={`border-b border-bg-border/60 ${!s.active ? 'opacity-50' : ''}`}
                >
                  <td className="p-3 text-white font-medium">
                    <span className="inline-flex items-center gap-2">
                      <MapPin size={14} className="text-gold-primary" />
                      {s.name}
                    </span>
                  </td>
                  <td className="p-3 text-text-secondary">{s.city || '—'}</td>
                  <td className="p-3 text-text-secondary">{s.address || '—'}</td>
                  <td className="p-3">
                    <span className={s.active ? 'text-success' : 'text-text-secondary'}>
                      {s.active ? 'Activa' : 'Inactiva'}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="flex justify-end gap-1">
                      <button
                        onClick={() => openEdit(s)}
                        title="Editar"
                        data-testid={`sede-edit-${s.id}`}
                        className="w-7 h-7 rounded flex items-center justify-center text-text-secondary hover:text-gold-primary"
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        onClick={() => toggleSede(s)}
                        title={s.active ? 'Desactivar' : 'Activar'}
                        data-testid={`sede-toggle-${s.id}`}
                        className={`w-7 h-7 rounded flex items-center justify-center text-text-secondary ${s.active ? 'hover:text-warning' : 'hover:text-success'}`}
                      >
                        <Power size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={modal}
        onClose={() => setModal(false)}
        title={editTarget ? 'Editar sede' : 'Nueva sede'}
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label className="label">Nombre del campo/polideportivo *</label>
            <input
              className="input"
              data-testid="sede-form-name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Campo Quatre Carreres"
            />
          </div>
          <div>
            <label className="label">Dirección completa</label>
            <input
              className="input"
              data-testid="sede-form-address"
              value={form.address}
              onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
              placeholder="Campo de Fútbol Quatre Carreres, Valencia"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Ciudad</label>
              <input
                className="input"
                data-testid="sede-form-city"
                value={form.city}
                onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
                placeholder="Valencia"
              />
            </div>
            <div>
              <label className="label">Estado</label>
              <label className="flex items-center gap-2 h-[42px] text-sm text-text-secondary cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="sede-form-active"
                  checked={form.active}
                  onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                />
                Activa
              </label>
            </div>
          </div>

          {saveError && (
            <p className="text-error text-xs bg-error/10 rounded p-2" data-testid="sede-save-error">
              {saveError}
            </p>
          )}

          <div className="flex justify-end gap-3">
            <button onClick={() => setModal(false)} className="btn-ghost">
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn-gold"
              data-testid="sede-save"
            >
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
